import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { computeRetentionDate } from "../../lib/multiSelectValue";

/**
 * 헤더 상시 계산기 — 두 칸으로 분리 (티켓 #35·#34)
 *   [디데이계산] 기준 날짜 · M+/D+ 토글 · 숫자입력 · 계산된 날짜
 *   [만나이계산] 생년월일 6자리 → 만 나이 자동 표시
 *
 * - 기준 날짜 기본값은 오늘의 YYMMDD. 매장 브라우저는 며칠씩 켜둔 채라 자정을 넘겨도 당일로
 *   유지돼야 하므로 마운트 1회 세팅이 아니라 주기적으로 "오늘"을 다시 확인한다
 *   (사용자가 손대면 자동 갱신 중지).
 * - 기준 날짜는 달력 대신 숫자 6자리로 받고, 항상 2000년대의 YYMMDD로 해석한다(티켓 #631).
 * - 단위(M/D)+숫자는 마지막 값만 localStorage 에 보관 — 서버 스키마 변경 없음.
 *
 * 2026-08-12: 통합검색을 같은 줄로 올리면서 이 계산기가 폭을 560px 가까이 먹어 자리가 없었다.
 * 기능은 그대로 두고 **글로 적혀 있던 힌트를 말풍선(title)·placeholder 로 옮겨** 폭만 줄였다
 * (숫자입력·생년월일6자리 안내, 중복이던 선택단위 표시). 헤더는 전 화면에 늘 붙어 있어
 * 여기서 아낀 폭이 모든 페이지에서 이득이다.
 *
 * 2026-08-13(티켓 #468): M+ 셈법이 신청서 서류함의 부가서비스 유지기간 칸과 달라 같은 M+12 가
 * 서로 다른 날짜를 냈다(헤더 2027-08-13 vs 서류함 2027-09-01). 여기서 자체 월말 보정을 하던
 * addMonths 를 버리고 **서류함이 쓰는 규칙 그대로**(computeRetentionDate) 계산한다 —
 * "기준월 +N개월째 달 말일의 다음날". D+ 는 두 곳이 원래 같은 동작이라 결과가 안 바뀌지만,
 * 규칙이 다시 두 벌로 갈라지지 않게 M·D 모두 같은 함수 한 곳을 거치게 했다.
 *
 * 2026-08-28: 그래도 펼친 폭이 572px 이라 상단 띠가 1440 이하에서 두 줄이 됐다. 기능은 하나도
 * 빼지 않고 접었다가, 곧바로 **두 계산기의 성격이 다르다**는 지적을 받아 다시 갈랐다:
 *   · **만나이** = 상담 중 수시로 치는 값 → 띠에 **상시 노출**(생년월일 칸 + 만N세).
 *   · **디데이** = 유지기간 따질 때만 → 칩 한 칸으로 접고, 올리거나 누르면 패널.
 * 접기/펼치기 규칙은 아래 주석 참조.
 */

const STORAGE_KEY = "pb.headerDateCalc.dday";
const LEGACY_STORAGE_KEY = "pb.headerDateCalc.expr";
const TICK_MS = 30_000;

type Unit = "M" | "D";

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function today6(): string {
  return toIsoDate(new Date()).slice(2).replace(/-/g, "");
}

/** "YYYY-MM-DD" → Date(로컬 정오 기준, DST/타임존 밀림 방지). 형식이 틀리면 null. */
function parseIsoDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(y, mo - 1, d, 12, 0, 0, 0);
  // 2026-02-31 같은 없는 날짜는 굴러떨어지므로 되돌려 대조
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}

/** 기준 날짜 6자리(YYMMDD) → 2000년대 Date. 덜 쳤거나 없는 날짜면 null. */
function parseDate6(value: string): Date | null {
  if (!/^\d{6}$/.test(value)) return null;
  return parseIsoDate(`20${value.slice(0, 2)}-${value.slice(2, 4)}-${value.slice(4, 6)}`);
}

/** 만 나이. 미래 날짜·비정상 값이면 null (표시 생략). */
function calcKoreanAge(birth: Date, now: Date): number | null {
  let age = now.getFullYear() - birth.getFullYear();
  const beforeBirthday =
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  if (age < 0 || age > 150) return null;
  return age;
}

/** 생년월일 6자리(YYMMDD) → Date. 두 자리 연도는 올해 아랫 두 자리 이하면 2000년대, 넘으면 1900년대. */
function parseBirth6(value: string, now: Date): Date | null {
  if (!/^\d{6}$/.test(value)) return null;
  const yy = Number(value.slice(0, 2));
  const century = yy <= now.getFullYear() % 100 ? 2000 : 1900;
  return parseIsoDate(`${century + yy}-${value.slice(2, 4)}-${value.slice(4, 6)}`);
}

function loadStoredDday(): { unit: Unit; amount: string } {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const m = /^([MD])\+(\d*)$/.exec(stored);
      if (m) return { unit: m[1] as Unit, amount: m[2] };
    }
    // 옛 자유 수식칸(M+6) 값 이관 — 더하기만 새 구조로 옮기고 옛 키는 지운다
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy !== null) {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      const m = /^\s*([MmDd])\s*\+\s*(\d+)\s*$/.exec(legacy);
      if (m) return { unit: m[1].toUpperCase() as Unit, amount: m[2] };
    }
  } catch {
    /* localStorage 접근 실패는 기본값으로 */
  }
  return { unit: "M", amount: "" };
}

const inputBase =
  "rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 " +
  "focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400/40 " +
  "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:[color-scheme:dark]";

const chipBase = "h-8 shrink-0 rounded-lg border px-2 text-xs font-semibold transition ";
const chipOff =
  chipBase +
  "border-gray-200 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.06]";
const chipOn = chipBase + "border-brand-500 bg-brand-500 text-white hover:bg-brand-600";

const HeaderDateCalc: React.FC<{ className?: string }> = ({ className = "" }) => {
  const [date6, setDate6] = useState<string>(() => today6());
  const [{ unit, amount }, setDday] = useState<{ unit: Unit; amount: string }>(() =>
    loadStoredDday(),
  );
  const [birth6, setBirth6] = useState<string>("");
  const [now, setNow] = useState<Date>(() => new Date());
  // 사용자가 날짜를 직접 만졌으면 자동 오늘 갱신을 멈춘다
  const touchedRef = useRef(false);
  const amountRef = useRef<HTMLInputElement>(null);

  // 자정 넘김 대비 — 주기적으로 오늘을 다시 확인
  useEffect(() => {
    const id = window.setInterval(() => {
      const current = new Date();
      setNow(current);
      if (!touchedRef.current) {
        const nextDate6 = toIsoDate(current).slice(2).replace(/-/g, "");
        setDate6((prev) => (prev === nextDate6 ? prev : nextDate6));
      }
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, `${unit}+${amount}`);
    } catch {
      /* 저장 실패는 무시 — 계산 자체엔 영향 없음 */
    }
  }, [unit, amount]);

  const baseDate = useMemo(() => parseDate6(date6), [date6]);
  const result = useMemo(() => {
    // 기준 날짜가 아직 온전하지 않으면(입력 중) 계산하지 않는다.
    // 셈법 자체는 서류함(부가서비스 유지기간)과 같은 함수 한 곳에서만 나온다.
    if (!baseDate || amount === "") return null;
    const calc = computeRetentionDate(`${unit}+${Number(amount)}`, toIsoDate(baseDate));
    return calc.ok && calc.date ? calc.date : null;
  }, [baseDate, unit, amount]);
  const age = useMemo(() => {
    const birth = parseBirth6(birth6, now);
    return birth ? calcKoreanAge(birth, now) : null;
  }, [birth6, now]);

  const selectUnit = useCallback((next: Unit) => {
    setDday((prev) => ({ ...prev, unit: next }));
    amountRef.current?.focus();
  }, []);

  // ── 접기/펼치기 (2026-08-28) ────────────────────────────────────────────────
  // 이 계산기는 펼친 채로 폭을 572px 먹어, 상단 띠가 1440 이하에서 두 줄로 접히는 주범이었다.
  // 기능은 그대로 두고 **평소엔 칩 한 칸, 필요할 때만 패널**로 바꾼다.
  //  · 마우스를 올리면 열리고(빠른 참조), 누르면 고정된다(타이핑 중 닫히면 못 쓴다).
  //  · 안쪽 입력칸에 커서가 있는 동안에도 고정 — 6자리를 치다 마우스가 살짝 벗어나면
  //    닫히는 게 이 도구에서 가장 나쁜 실패다.
  //  · 결과(계산된 날짜·만나이)는 접힌 칩에 그대로 찍는다 — 펴지 않고 눈으로 보던 이점을
  //    잃지 않으려는 것이다.
  //  · 패널은 띠 아래에 떠 있는다(absolute) — 열고 닫을 때 옆 요소가 밀리지 않는다.
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  // 마우스가 칩과 패널 사이를 지날 때 잠깐 바깥으로 나가므로 유예를 둔다(즉시 닫으면 깜빡인다).
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 180);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  // 바깥 클릭·Esc 로 닫는다(고정도 함께 푼다).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
      setPinned(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      setPinned(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const resultText = result ? toIsoDate(result).slice(2) : "";
  const ageText = age === null ? "" : `만${age}세`;

  return (
    // 두 계산기는 성격이 다르다(2026-08-28 재배치):
    //  · **만나이**는 상담 중 수시로 치는 값이라 **띠에 그대로 노출**한다 — 한 번의 클릭도 아깝다.
    //  · **디데이**는 유지기간을 따질 때만 쓰므로 칩으로 접어 두고 필요할 때만 편다.
    <div className={`shrink-0 items-center gap-2 ${className}`}>
      {/* ── 디데이 계산기 (접힘) ── */}
      <div
        ref={rootRef}
        className="relative shrink-0"
        onMouseEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onMouseLeave={() => {
          if (!pinned) scheduleClose();
        }}
      >
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          title="디데이 계산기 — 기준일에 개월·일을 더한 날짜. 누르면 고정됩니다"
          onClick={() => {
            setOpen(true);
            setPinned((prev) => !prev);
          }}
          className={`flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-xs font-semibold transition ${
            open || pinned
              ? "border-brand-500 text-brand-600 dark:text-brand-300"
              : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.06]"
          }`}
        >
          <span aria-hidden>🗓</span>
          {/* '디데이'로만 줄였더니 무엇을 계산하는 칸인지 안 읽혔다(2026-08-28 지적) —
              폭을 아끼더라도 이름은 온전히 둔다. */}
          <span>디데이 계산기</span>
          {resultText && (
            <span className="font-semibold text-brand-600 dark:text-brand-300">{resultText}</span>
          )}
        </button>

        {open && (
          <div
            role="dialog"
            aria-label="디데이 계산기"
            // 입력칸에 커서가 들어오면 마우스가 어디에 있든 열어 둔다.
            onFocusCapture={() => {
              cancelClose();
              setPinned(true);
            }}
            className="absolute left-0 top-full z-99999 mt-1 flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-900"
          >
            <input
              type="text"
              inputMode="numeric"
              value={date6}
              onChange={(e) => {
                touchedRef.current = true;
                setDate6(e.target.value.replace(/\D/g, "").slice(0, 6));
              }}
              aria-label="기준 날짜"
              placeholder="261219"
              title="기준 날짜 6자리 (기본값 오늘)"
              className={`${inputBase} h-8 w-[5rem] shrink-0 text-center`}
            />

            <button
              type="button"
              onClick={() => selectUnit("M")}
              aria-pressed={unit === "M"}
              title="개월 더하기"
              className={unit === "M" ? chipOn : chipOff}
            >
              M+
            </button>
            <button
              type="button"
              onClick={() => selectUnit("D")}
              aria-pressed={unit === "D"}
              title="일 더하기"
              className={unit === "D" ? chipOn : chipOff}
            >
              D+
            </button>

            {/* 선택 단위를 따로 적던 칸은 없앴다 — M+/D+ 칩이 이미 눌린 상태로 같은 걸 보여준다. */}
            <input
              ref={amountRef}
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(e) =>
                setDday((prev) => ({
                  ...prev,
                  amount: e.target.value.replace(/\D/g, "").slice(0, 4),
                }))
              }
              placeholder="숫자"
              aria-label="숫자입력"
              title={
                unit === "M"
                  ? "기준월 +N개월째 달 말일의 다음날"
                  : "숫자만 넣으면 일 뒤 날짜가 계산됩니다"
              }
              className={`${inputBase} h-8 w-[3.5rem] shrink-0 text-center`}
            />

            <span
              aria-label="계산된 날짜"
              className="min-w-[4.75rem] shrink-0 text-center text-xs font-semibold text-gray-700 dark:text-gray-200"
            >
              {resultText}
            </span>
          </div>
        )}
      </div>

      {/* ── 만나이 계산기 (상시 노출) ── */}
      {/* 한때 라벨을 빼고 placeholder('생년월일')만 뒀는데, **무슨 계산인지 알 수 없다**는
          지적을 받아 되살렸다(2026-08-28) — 띠에서 폭을 아끼는 것보다 이게 먼저다. */}
      <span className="shrink-0 whitespace-nowrap text-xs font-semibold text-gray-700 dark:text-gray-200">
        만나이 계산기
      </span>
      <input
        type="text"
        inputMode="numeric"
        value={birth6}
        onChange={(e) => setBirth6(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="생년월일"
        aria-label="생년월일 6자리"
        title="생년월일 6자리(예: 841102)를 넣으면 만 나이가 바로 계산됩니다"
        className={`${inputBase} h-9 w-[6rem] shrink-0 text-center`}
      />
      <span
        aria-label="만 나이"
        title="오늘 기준 만 나이"
        className="min-w-[2.75rem] shrink-0 text-center text-xs font-semibold text-gray-700 dark:text-gray-200"
      >
        {ageText}
      </span>
    </div>
  );
};

export default HeaderDateCalc;
